import {useMemo, useRef, useState} from 'react';
import {Box, IconButton, makeStyles, Menu, MenuItem, Tooltip} from "@material-ui/core";
import {useDispatch, useSelector} from "react-redux";
import {changeGraphType, setFilter} from "../../../../../../store/actions/creator/creatorExperienceManager";
import UtilService from '../../../../../../helper/service';
import {
  changeRealTimeGraphType,
  fetchSessionMarker,
  updateSettings
} from '../../../../../../store/actions/creator/analysis';
import useWindowSize from '../../../../../../helper/hooks/useWindowSize';
import XLSXDownloadBtn from '../../../../../atoms/creator/analyzeXlsxDownBtn';
import {useStylesTooltip} from "../../../../../../config/theme";
import AnalyzeManualNoteBtn from '../../../../../atoms/creator/analyzeManualNoteBtn';
import { useFeature } from 'flagged';

const useStyles = makeStyles(() => ({
  root: {
    position: 'fixed',
    minHeight: 48,
    paddingTop: 3,
    width: '100%',
    backgroundColor: '#FFFFFF',
    borderBottom: '1px solid #D9D9D9',
    display: 'flex',
    alignItems: 'center',
    zIndex: 1001
  },
  menuItem: {
    padding: 0,
    '& .MuiPaper-root': {
      textDecoration: 'none'
    }
  },
  graphType: {
    fontSize: 14,
    fontFamily: 'roboto-bold',
    color: '#017AEF',
    marginLeft: 22,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row'
  },
  filter: {
    fontSize: 14,
    fontFamily: 'roboto-bold',
    marginLeft: 30,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    '& img': {
      marginRight: 10
    }
  },
  btn: {
    fontSize: 14,
    fontFamily: 'roboto-regular',
    color: '#697683',
    textTransform: 'unset',
    marginRight: 20,
    marginLeft: 20,
    width: '100%',
    display: 'flex',
    alignItems: 'center'
  },
  iconBtn: {
    width: 40,
    borderRadius: 10,
    '&:hover': {
      background: '#017AEF14'
    },
  },
  icon: {
    width: 14,
    height: 14
  },
  zoomActive: {
    alignItems: 'flex-end',
    marginRight: 90,
    '& img': {
      padding: '4px 0',
    }
  }
}));

function AnalysisHeader() {

  const hasFeatureClip = useFeature('feature_clip');


  const classes = useStyles();
  const tooltipClasses = useStylesTooltip();
  const dispatch = useDispatch();
  const anchorRef = useRef(null);
  const realtimeRef = useRef(null);

  const { filter, graphType } = useSelector((state: any) => state.creatorExperienceManager);
  const { all, cliped, stimulus, settings } = useSelector((state: any) => state.analysis);
  const { clip, graphZoom, pdfLoading, videoPopIn } = settings;

  const [openLeftMenu, setOpenLeftMenu] = useState(false);

  const { currentSession, realTimeGraphType, analyseStart, analyseEnd } = useSelector((state: any) => state.analysis);
  const { session_status, session_type, session_code, session_length } = currentSession;
  const apolloClient = useSelector((state: any) => state.apolloClient);

  const { width } = useWindowSize();
  const paneWidth = (width && width > 1200) ? (width - (!clip ? 600 : 200)) : 600;

  const graphLength = useMemo(() => {
    if (cliped && stimulus) return cliped.end_clip - cliped.start_clip;
    if (stimulus && session_type === 'ASYNC') return stimulus.stimulus_video_length;
    if (cliped) return cliped.end_clip - cliped.start_clip;
    return analyseEnd - analyseStart
  }, [stimulus, cliped, analyseEnd, analyseStart]);

  const minZoomDetect = graphZoom === 1 ? 100 : (graphLength * 15 / Math.pow(Math.ceil(graphZoom) - 1, 2) / paneWidth);
  const maxZoom = Math.sqrt(graphLength * 15 / paneWidth);
  const graphEdge = 28;

  const headerOptions = useMemo(() => {
    const length = cliped ? Number(cliped.end_clip - cliped.start_clip) : Number(session_length);
    if (UtilService.isShowSafety(session_type, Number(length))) {
      if (stimulus && stimulus.stimulus_video_length < 121 && session_type === 'ASYNC') {
        return ['Immersion']
      } else {
        return ['Immersion', 'Safety', 'Immersion & Safety']
      }
    } else {
      return ['Immersion']
    }
  }, [stimulus, session_length, session_type, cliped]);

  const handleMenuLeftOpen = () => {
    setOpenLeftMenu(true);
  };

  const handleMenuLeftClose = () => {
    setOpenLeftMenu(false);
  };

  const handleGraphType = (type: number) => {
    dispatch(changeGraphType(type));
    handleMenuLeftClose();
  };

  const handleChangeRealGraphType = (type: number) => {
    dispatch(changeRealTimeGraphType(type));
    handleMenuLeftClose();
  };

  const handleFilter = () => {
    dispatch(setFilter(!filter));
  };

  const onZoomWidth = (zoomable: boolean) => {
    let zoom = 0;
    if (!zoomable && graphZoom < Math.ceil(maxZoom)) {
      zoom = maxZoom
    }
    if (!zoomable && graphZoom < Math.floor(maxZoom)) {
      zoom = Math.ceil(graphZoom) + 1;
    }
    if (zoomable && graphZoom >= 2) {
      if(minZoomDetect < graphEdge){
        zoom = Math.ceil(graphZoom) - 1;
      }
    }
    if (zoomable && graphZoom > 1 && graphZoom < 2) {
      zoom = clip ? maxZoom : 1;
    }


    if (zoom !== 0) {
      const graphStart = cliped ? cliped.start_clip : (stimulus && session_type === 'ASYNC') ? 0 : analyseStart;
      dispatch(updateSettings('graphZoom', zoom));
      dispatch(updateSettings('xScroll', (videoPopIn + ((stimulus && session_type === 'ASYNC') ? 0 : analyseStart) - graphStart - 15) * 15 / Math.pow(zoom < 1 ? 1 : zoom, 2)))
    }

  };

  return (
    <div className={classes.root} style={{ paddingTop: pdfLoading ? 0 : 3, minHeight: pdfLoading ? 40 : 48 }}>
      {(session_status === 'COMPLETED' || session_type === 'ASYNC') && <div className={classes.graphType} ref={anchorRef} onClick={handleMenuLeftOpen}>
        {graphType === 0 && <img src={'/assets/icons/liveanalysis/header-immersion-icon.svg'} alt={'Arrow'} />}
        {graphType !== 0 && <img src={'/assets/icons/liveanalysis/header-safety-icon.svg'} alt={'Arrow'} style={{ width: 15, height: 16 }} />}
        <div style={{ whiteSpace: 'nowrap', marginLeft: 4 }}>{UtilService.getSubHeader(graphType)}</div>
        <img src={'/assets/icons/liveanalysis/graph-toggle-arrow.svg'} alt={'Arrow'} style={{ marginLeft: 10 }} />
      </div>}

      {session_status === 'LIVE' && session_type !== 'ASYNC' &&
        <div className={classes.graphType} ref={realtimeRef} onClick={handleMenuLeftOpen}>
          <div style={{ whiteSpace: 'nowrap' }}>{UtilService.getSubRealTimeHeader(realTimeGraphType)}</div>
          <img src={'/assets/icons/liveanalysis/graph-toggle-arrow.svg'} alt={'Arrow'} style={{ marginLeft: 10 }} />
        </div>}

      <div
        className={classes.filter}
        onClick={() => handleFilter()}
        style={filter ? { color: '#017AEF' } : { color: '#697683' }}
      >
        <img src={`/assets/icons/liveanalysis/${filter ? `filter-select-icon.svg` : `filter-unselect-icon.svg`}`}
          alt={'Arrow'} />
        Filters
      </div>

      <Box display='flex' flex={1} />

      {
        all.length !== 0 &&
        <div className={classes.zoomActive}>
          {(session_status !== 'LIVE' || session_type === 'ASYNC') && graphZoom >= 1 && (
            <Tooltip classes={tooltipClasses} title="Zoom In" placement="bottom" arrow>
              <IconButton
                className={classes.iconBtn}
                color="primary"
                size='small'
                disableRipple
                onClick={() => onZoomWidth(true)}
              >
                <img
                  src={'/assets/icons/liveanalysis/zoom-in-active-btn.svg'}
                  alt='img'
                  style={{ opacity: (graphZoom === 1 || minZoomDetect >= graphEdge) ? 0.5 : 1 }}
                />
              </IconButton>
            </Tooltip>
          )}

          {(session_status !== 'LIVE' || session_type === 'ASYNC') && graphZoom >= 1 && (
            <Tooltip classes={tooltipClasses} title="Zoom Out" placement="bottom" arrow>
              <IconButton
                className={classes.iconBtn}
                color="primary"
                size='small'
                disableRipple
                onClick={() => onZoomWidth(false)}
              >
                <img
                  src={'/assets/icons/liveanalysis/zoom-out-inactive-btn.svg'}
                  alt='img'
                  style={{ padding: '4px 0', opacity: graphZoom === maxZoom ? 0.5 : 1 }}
                />
              </IconButton>
            </Tooltip>
          )}

          {!cliped && (session_status === 'COMPLETED' || session_type === 'ASYNC') && (
            <Tooltip classes={tooltipClasses} title="Clip" placement="bottom" arrow>

              { hasFeatureClip ? 
              <IconButton
                className={classes.iconBtn}
                color="primary"
                size='small'
                disableRipple
                style={{ marginLeft: 20 }}
                onClick={() => {
                  dispatch(updateSettings('clip', !clip))
                  dispatch(updateSettings('videoPlayed', 0))
                  !clip && dispatch(updateSettings('addNoteX', -1))
                  !clip && dispatch(updateSettings('addNoteY', -1))
                  !clip && dispatch(fetchSessionMarker(apolloClient.client, { session_code }));
                }
                }
              >
                
                
                <img src={`/assets/icons/liveanalysis/header-scissors-${clip ? 'blue' : 'grey'}.svg`} alt='img' />
              </IconButton>
              :
              <IconButton
                className={classes.iconBtn}
                color="primary"
                size='small'
                disableRipple
                style={{ marginLeft: 20 }}
              >
                
                <img src={`/assets/icons/liveanalysis/header-scissors-disabled.svg`} alt='img' />
              </IconButton>

              }
            </Tooltip>
          )}

          {(session_status === 'COMPLETED' || session_type === 'ASYNC') &&
            <AnalyzeManualNoteBtn />
          }

          <XLSXDownloadBtn clipped={cliped}/>

        </div>
      }

      {(session_status === 'COMPLETED' || session_type === 'ASYNC') && <Menu
        anchorEl={anchorRef.current}
        elevation={1}
        onClose={handleMenuLeftClose}
        open={openLeftMenu}
        PaperProps={{
          style: { width: 'auto', marginTop: 50, borderRadius: 12 }
        }}
      >
        {headerOptions.map((option, index) => (
          <MenuItem
            className={classes.menuItem}
            key={option}
            onClick={() => handleGraphType(index)}
          >
            <p className={classes.btn}>{option}</p>
          </MenuItem>
        ))}
      </Menu>}

      {session_status === 'LIVE' && session_type === 'MODERATED' && <Menu
        anchorEl={realtimeRef.current}
        elevation={1}
        onClose={handleMenuLeftClose}
        open={openLeftMenu}
        PaperProps={{
          style: { width: 'auto', marginTop: 50, borderRadius: 12 }
        }}
      >
        {['Overall Immersion', 'Real-Time Immersion'].map((option, index) => (
          <MenuItem
            className={classes.menuItem}
            key={option}
            onClick={() => handleChangeRealGraphType(index)}
          >
            <p className={classes.btn}>{option}</p>
          </MenuItem>
        ))}
      </Menu>}

    </div>
  )
}

export default AnalysisHeader;